import { Select, SelectProps, Switch, SwitchProps } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import {
  AGGREGATION_OPTIONS,
  AGGREGATIONS_CONFIG,
  AggregationOption,
  AggregationTransform,
  CalculationType,
  TableSpec,
  TableThresholdOptions,
  TransformKind,
  UNIT_SETTINGS_NAME,
} from '@/core';
import {
  OptionsEditorColumn,
  OptionsEditorControl,
  OptionsEditorGrid,
  OptionsEditorGroup,
  ResetSettingsButton,
  SettingsAutocomplete,
  SettingsAutocompleteOption,
  UnitSelector,
} from '@/plugin-editor-components';
import { OptionsEditorProps } from '@/plugin-system';
import { TableSortEditor, TableSortEditorProps } from './TableSortEditor';
import { TableThresholdsEditor } from './TableThresholdsEditor';
import {
  DEFAULT_TRANSFORM,
  DEFAULT_UNIT,
  NO_TRANSFORM_OPTION,
  TABLE_FORMAT_OPTIONS,
  TransformType,
} from './table-model';
import { useTableRowsAndColumns } from './utils';

export type TableOptionsEditorProps = OptionsEditorProps<TableSpec>;

export function TableOptionsEditorSettings(props: TableOptionsEditorProps) {
  const { onChange, value } = props;

  const { columns } = useTableRowsAndColumns(value);

  const colOptions = columns.map((col) => ({ id: col.field, label: col.headerName ?? '' }));
  const handleTimeSeriesFormatChange: SelectProps['onChange'] = (e, selectedOption) => {
    onChange(
      produce(value, (draft: TableSpec): void => {
        if (selectedOption?.key === NO_TRANSFORM_OPTION.key) {
          delete draft.transform;
        } else if (selectedOption?.key) {
          if (selectedOption.key === DEFAULT_TRANSFORM.kind) {
            draft.transform = DEFAULT_TRANSFORM;
          } else {
            draft.transform = { kind: selectedOption.key as TransformType };
          }
        } else {
          draft.transform = DEFAULT_TRANSFORM;
        }

        // reset sort if format changes
        if (value.sort) {
          draft.sort = undefined;
        }

        // reset column overrides if format changes
        if (value.columnOverrides) {
          draft.columnOverrides = undefined;
        }
      })
    );
  };

  const handleConvertToLabelChange: SwitchProps['onChange'] = (_, checked) => {
    onChange(
      produce(value, (draft: TableSpec) => {
        if (draft.transform?.kind === TransformKind.Aggregation) {
          draft.transform.labelsToColumns = checked;
        }
      })
    );
  };

  const handleCalculationsChange = (_: unknown, values: SettingsAutocompleteOption[]) => {
    onChange(
      produce(value, (draft: TableSpec) => {
        const stats = values?.map((v) => v.id as CalculationType) ?? [];
        if (draft.transform && 'stats' in draft.transform) {
          draft.transform.stats = stats;
        } else {
          draft.transform = {
            kind: DEFAULT_TRANSFORM.kind,
            labelsToColumns: (draft.transform as AggregationTransform).labelsToColumns,
            stats,
          };
        }
      })
    );
  };

  const handleThresholdsChange = (thresholds: TableThresholdOptions) => {
    onChange(
      produce(value, (draft: TableSpec) => {
        if (thresholds.default_color !== undefined && thresholds.base !== undefined) {
          delete thresholds.default_color;
        }

        draft.thresholds = thresholds;
      })
    );
  };

  const handleSortChange: TableSortEditorProps['onChange'] = (newSort) => {
    onChange(
      produce(value, (draft: TableSpec) => {
        draft.sort = newSort;
      })
    );
  };

  const aggregationStats = value?.transform
    ? (value?.transform as AggregationTransform).stats?.reduce((result, item) => {
        const config = AGGREGATIONS_CONFIG[item];
        if (config) {
          result.push({ ...config, id: item });
        }
        return result;
      }, [] as AggregationOption[])
    : [];

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Data Format">
          <OptionsEditorControl
            label="Time series format"
            control={
              <Select
                disableClearable
                onChange={handleTimeSeriesFormatChange}
                value={TABLE_FORMAT_OPTIONS.find((o) => o.key === value.transform?.kind) ?? NO_TRANSFORM_OPTION}
                options={[NO_TRANSFORM_OPTION, ...TABLE_FORMAT_OPTIONS]}
              />
            }
          />

          {value.transform?.kind === 'Aggregation' && (
            <>
              <OptionsEditorControl
                label="Calculations"
                control={
                  <SettingsAutocomplete
                    multiple
                    disableCloseOnSelect
                    disableClearable
                    value={aggregationStats}
                    options={AGGREGATION_OPTIONS}
                    onChange={handleCalculationsChange}
                    disabled={value === undefined}
                    limitTags={1}
                    ChipProps={{
                      size: 'small',
                    }}
                  />
                }
              />
              <OptionsEditorControl
                label="Label-based columns"
                description="Create a column for each label in the series"
                control={
                  <Switch checked={value.transform?.labelsToColumns ?? false} onChange={handleConvertToLabelChange} />
                }
              />
            </>
          )}
        </OptionsEditorGroup>
        <TableSortEditor sort={value.sort} options={colOptions} onChange={handleSortChange} />
      </OptionsEditorColumn>

      <OptionsEditorColumn>
        <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
          <UnitSelector
            value={props.value.unit || DEFAULT_UNIT}
            onChange={(v) => {
              props.onChange({
                ...props.value,
                unit: v,
              });
            }}
          />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <TableThresholdsEditor thresholds={value.thresholds} onChange={handleThresholdsChange} />
        <ResetSettingsButton value={value} onChange={onChange} />
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
